/**
 * BillingPage — the main /billing page.
 *
 * Shows: current plan + subscription status, payment methods (add/remove/
 * set default), invoice history, and credit ledger.
 *
 * Replaces the old BillingPage.tsx which only had plan cards + Checkout
 * redirect + a Stripe Portal button.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  CreditCard,
  Trash2,
  Star,
  Plus,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { StripeProvider } from "@/components/billing/StripeProvider.tsx";
import { billingApi } from "@/api/billing.ts";
import { creditsApi } from "@/api/credits.ts";
import { authApi } from "@/api/auth.ts";
import { useAuth } from "@/hooks/useAuth.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { useToast } from "@/stores/toastStore.ts";

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatUnix(seconds: number): string {
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function brandName(brand?: string | null): string {
  if (!brand) return "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

// ── Add Card form (inside StripeProvider) ────────────────────────────────────

function AddCardForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message || "Card details invalid");
        setSubmitting(false);
        return;
      }
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + "/billing?cardAdded=1",
        },
        redirect: "if_required",
      });
      if (error) {
        toast.error(error.message || "Could not save card");
        setSubmitting(false);
        return;
      }
      toast.success("Card saved");
      onSuccess();
    } catch (err: any) {
      toast.error(err?.message || "Could not save card");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement options={{ layout: "tabs" }} />
      <div className="flex gap-2">
        <Button variant="primary" className="flex-1" loading={submitting} onClick={handleSubmit}>
          {submitting ? "Saving…" : "Save card"}
        </Button>
        <Button variant="outline" disabled={submitting} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export function BillingPage() {
  const { user } = useAuth();
  const updateUser = useAuthStore((s) => s.updateUser);
  const toast = useToast();
  const [searchParams] = useSearchParams();

  // State
  const [subscription, setSubscription] = useState<any>(null);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [ledger, setLedger] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add card flow
  const [addingCard, setAddingCard] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [canceling, setCanceling] = useState(false);

  // Show success toast if redirected from subscribe
  useEffect(() => {
    if (searchParams.get("subscribed") === "1") {
      toast.success("You're subscribed!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const [subRes, pmRes, invRes, balRes, ledRes] = await Promise.allSettled([
        billingApi.getSubscription(),
        billingApi.paymentMethods(),
        billingApi.invoices(),
        creditsApi.balance(),
        creditsApi.ledger({ limit: 10 }),
      ]);
      if (subRes.status === "fulfilled") setSubscription(subRes.value);
      if (pmRes.status === "fulfilled") setPaymentMethods(pmRes.value.methods ?? []);
      if (invRes.status === "fulfilled") setInvoices(invRes.value.invoices ?? []);
      if (balRes.status === "fulfilled") setBalance(balRes.value.balance ?? 0);
      if (ledRes.status === "fulfilled") setLedger(ledRes.value.entries ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  // ── Handlers ────────────────────────────────────────────────────────

  const handleStartAddCard = async () => {
    try {
      const res = await billingApi.setupIntent();
      setSetupClientSecret(res.clientSecret);
      setAddingCard(true);
    } catch (err: any) {
      toast.error(err?.message || "Could not start card setup");
    }
  };

  const handleCardAdded = async () => {
    setAddingCard(false);
    setSetupClientSecret(null);
    await reload();
  };

  const handleRemoveCard = async (id: string) => {
    if (!confirm("Remove this card?")) return;
    try {
      await billingApi.removePaymentMethod(id);
      toast.success("Card removed");
      await reload();
    } catch (err: any) {
      toast.error(err?.message || "Could not remove card");
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await billingApi.setDefaultPaymentMethod(id);
      toast.success("Default card updated");
      await reload();
    } catch (err: any) {
      toast.error(err?.message || "Could not update default");
    }
  };

  const handleCancel = async () => {
    if (
      !confirm(
        "Cancel your subscription? It will remain active until the end of the current billing period.",
      )
    )
      return;
    setCanceling(true);
    try {
      await billingApi.cancelSubscription();
      toast.success("Subscription will cancel at period end");
      await reload();
      // Refresh user
      const me = await authApi.me();
      updateUser(me.user);
    } catch (err: any) {
      toast.error(err?.message || "Could not cancel");
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivate = async () => {
    try {
      await billingApi.reactivateSubscription();
      toast.success("Subscription reactivated");
      await reload();
      const me = await authApi.me();
      updateUser(me.user);
    } catch (err: any) {
      toast.error(err?.message || "Could not reactivate");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  const videoCredits = Math.floor(balance / 100);
  const isFreeTrial = !user?.currentPlan;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8">
      <h1 className="text-2xl font-bold text-fg">Billing</h1>

      {/* ── Current plan ──────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm text-fgMuted">Current plan</p>
              <p className="mt-1 text-2xl font-bold capitalize text-fg">
                {user?.currentPlan || "Free Trial"}
              </p>
              <p className="mt-1 text-sm text-fgMuted">
                {videoCredits} video credit{videoCredits === 1 ? "" : "s"} remaining
              </p>
            </div>
            <div className="flex gap-2">
              {isFreeTrial ? (
                <Link to="/subscribe">
                  <Button variant="primary">Upgrade</Button>
                </Link>
              ) : (
                <Link to="/subscribe">
                  <Button variant="outline">Change plan</Button>
                </Link>
              )}
            </div>
          </div>

          {subscription && (
            <div className="mt-6 space-y-3 border-t border-border pt-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-fgMuted">Status</span>
                <span className="font-medium capitalize text-fg">{subscription.status}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-fgMuted">
                  {subscription.cancel_at_period_end ? "Cancels on" : "Renews on"}
                </span>
                <span className="font-medium text-fg">
                  {formatUnix(subscription.current_period_end ?? 0)}
                </span>
              </div>

              {subscription.cancel_at_period_end ? (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium">This subscription is set to cancel.</p>
                    <p className="mt-1">
                      You'll keep access until{" "}
                      {formatUnix(subscription.current_period_end ?? 0)}.
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleReactivate}>
                    Reactivate
                  </Button>
                </div>
              ) : (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={canceling}
                    className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    {canceling ? "Canceling…" : "Cancel subscription"}
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Payment methods ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Payment methods</CardTitle>
            {!addingCard && (
              <Button variant="outline" size="sm" onClick={handleStartAddCard}>
                <Plus className="h-4 w-4" /> Add card
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {paymentMethods.length === 0 && !addingCard && (
            <p className="text-sm text-fgMuted">No payment methods on file.</p>
          )}

          {paymentMethods.length > 0 && (
            <ul className="divide-y divide-border">
              {paymentMethods.map((pm) => (
                <li
                  key={pm.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-fgMuted" />
                    <div>
                      <p className="text-sm font-medium text-fg">
                        {brandName(pm.brand)} •••• {pm.last4 ?? "????"}
                      </p>
                      <p className="text-xs text-fgMuted">
                        Expires {String(pm.expMonth).padStart(2, "0")}/{pm.expYear}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleSetDefault(pm.id)}
                      title="Set as default"
                      className="rounded-lg p-2 text-fgMuted hover:bg-brand-500/10 hover:text-brand-600 transition-colors"
                    >
                      <Star className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveCard(pm.id)}
                      title="Remove"
                      className="rounded-lg p-2 text-fgMuted hover:bg-red-500/10 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {addingCard && setupClientSecret && (
            <div className="mt-4 border-t border-border pt-4">
              <StripeProvider clientSecret={setupClientSecret}>
                <AddCardForm
                  onSuccess={handleCardAdded}
                  onCancel={() => {
                    setAddingCard(false);
                    setSetupClientSecret(null);
                  }}
                />
              </StripeProvider>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Invoices ──────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-sm text-fgMuted">No invoices yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {invoices.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div>
                    <p className="text-sm font-medium text-fg">
                      {formatUnix(inv.created)}
                    </p>
                    <p className="text-xs capitalize text-fgMuted">
                      {inv.status || "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-fg">
                      ${(inv.amount / 100).toFixed(2)}
                    </span>
                    {inv.pdfUrl && (
                      <a
                        href={inv.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
                      >
                        PDF <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ── Credit ledger ─────────────────────────────────────────────── */}
      {ledger.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent credit activity</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {ledger.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between px-6 py-3"
                >
                  <div>
                    <p className="text-sm font-medium capitalize text-fg">
                      {entry.reason.replace(/_/g, " ")}
                    </p>
                    <p className="text-xs text-fgMuted">
                      {new Date(entry.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={
                      entry.delta >= 0
                        ? "font-mono text-sm text-success"
                        : "font-mono text-sm text-fgMuted"
                    }
                  >
                    {entry.delta >= 0 ? "+" : ""}
                    {entry.delta}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
